import React, { useState } from "react";
import { Phone, Mail, Globe, MapPin, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { profile, services } from "../data/mock";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (key, v) => setForm((f) => ({ ...f, [key]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    // Mock submission — stored in localStorage for now
    try {
      const list = JSON.parse(localStorage.getItem("hb_inquiries") || "[]");
      list.push({ ...form, ts: new Date().toISOString() });
      localStorage.setItem("hb_inquiries", JSON.stringify(list));
      await new Promise((r) => setTimeout(r, 700));
      toast.success("Thank you. Dr. Barot's office will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section bg-[#F8F5EE]">
      <div className="max-w-7xl mx-auto container-px grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left info */}
        <div className="lg:col-span-5 reveal">
          <span className="eyebrow">Get in Touch</span>
          <h2 className="h-section text-3xl lg:text-5xl mt-6">
            Plan, accredit or transform
            <br />
            <span className="italic">your hospital with us.</span>
          </h2>
          <div className="divider-gold" />
          <p className="text-[15px] text-[#0B1E3F]/75 leading-relaxed max-w-md">
            Tell us a little about your hospital and the outcome you're aiming for. We typically respond within one working day.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${profile.contact.phone.replace(/\s/g, "")}`} className="flex items-start gap-4 group">
              <span className="w-10 h-10 border border-[#0B1E3F]/20 flex items-center justify-center text-[#0B1E3F] group-hover:bg-[#0B1E3F] group-hover:text-[#C9A961] transition-colors">
                <Phone size={16} />
              </span>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#0B1E3F]/55">Phone</div>
                <div className="text-[15px] text-[#0B1E3F] mt-0.5">{profile.contact.phone}</div>
              </div>
            </a>
            <a href={`mailto:${profile.contact.email}`} className="flex items-start gap-4 group">
              <span className="w-10 h-10 border border-[#0B1E3F]/20 flex items-center justify-center text-[#0B1E3F] group-hover:bg-[#0B1E3F] group-hover:text-[#C9A961] transition-colors">
                <Mail size={16} />
              </span>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#0B1E3F]/55">Email</div>
                <div className="text-[15px] text-[#0B1E3F] mt-0.5">{profile.contact.email}</div>
              </div>
            </a>
            <a href={`https://${profile.contact.website}`} target="_blank" rel="noreferrer" className="flex items-start gap-4 group">
              <span className="w-10 h-10 border border-[#0B1E3F]/20 flex items-center justify-center text-[#0B1E3F] group-hover:bg-[#0B1E3F] group-hover:text-[#C9A961] transition-colors">
                <Globe size={16} />
              </span>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#0B1E3F]/55">Website</div>
                <div className="text-[15px] text-[#0B1E3F] mt-0.5">{profile.contact.website}</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[#0B1E3F]/20 flex items-center justify-center text-[#0B1E3F]">
                <MapPin size={16} />
              </span>
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#0B1E3F]/55">Office</div>
                <div className="text-[14px] text-[#0B1E3F]/85 mt-0.5 leading-relaxed max-w-xs">
                  {profile.contact.address}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <form onSubmit={onSubmit} className="lg:col-span-7 bg-white border border-[#0B1E3F]/10 p-8 lg:p-10 reveal">
          <h3 className="font-serif text-2xl text-[#0B1E3F]">Request a consultation</h3>
          <p className="text-[13px] text-[#0B1E3F]/60 mt-2">All fields marked * are required.</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[12px] tracking-[0.15em] uppercase text-[#0B1E3F]/70">Full Name *</Label>
              <Input id="name" value={form.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Dr. / Mr. / Ms." className="rounded-none border-[#0B1E3F]/20 focus-visible:ring-[#C9A961]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[12px] tracking-[0.15em] uppercase text-[#0B1E3F]/70">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => onChange("email", e.target.value)} placeholder="you@hospital.com" className="rounded-none border-[#0B1E3F]/20 focus-visible:ring-[#C9A961]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[12px] tracking-[0.15em] uppercase text-[#0B1E3F]/70">Phone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="+91" className="rounded-none border-[#0B1E3F]/20 focus-visible:ring-[#C9A961]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px] tracking-[0.15em] uppercase text-[#0B1E3F]/70">Service of interest</Label>
              <Select value={form.interest} onValueChange={(v) => onChange("interest", v)}>
                <SelectTrigger className="rounded-none border-[#0B1E3F]/20 focus:ring-[#C9A961]">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s, i) => (
                    <SelectItem key={i} value={s.title}>{s.title}</SelectItem>
                  ))}
                  <SelectItem value="Other">Other / Bespoke</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <Label htmlFor="message" className="text-[12px] tracking-[0.15em] uppercase text-[#0B1E3F]/70">How can we help? *</Label>
            <Textarea id="message" rows={5} value={form.message} onChange={(e) => onChange("message", e.target.value)} placeholder="Brief us on your hospital and what you'd like to achieve..." className="rounded-none border-[#0B1E3F]/20 focus-visible:ring-[#C9A961]" />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary mt-8 disabled:opacity-60">
            {submitting ? "Sending..." : "Send Inquiry"}
            <Send size={16} />
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
